/* gr-discovery-kit.js — Discovery Kit behavior.
 *
 * Cart submission:
 *   POST /cart/add.js with:
 *     id: kit parent variant id (from section.settings.kit_variant_id)
 *     quantity: 1
 *     selling_plan: plan id when subscribed
 *     properties:
 *       _bundle_children: JSON [{id, title, qty}, ...]   ← 3 curated + mug (+optional 4th)
 *       _kit_grind: 'whole' | 'ground'
 *       _kit_has_fourth: '1' | '0'
 *       Items: human-readable list
 *
 *   A Shopify Cart Transform Function (same one as bundle-builder, see
 *   data/bundle-cart-transform.md) reads _bundle_children and expands the
 *   kit line into real child line items at checkout. The mug child should
 *   be set to price 0 by the Function (it's free inside the kit).
 */

(() => {
  const root = document.querySelector('.gr-dk');
  if (!root) return;

  const saveLabel = root.dataset.saveLabel || 'YOU SAVE';
  const i18n = {
    savePill: root.dataset.i18nSavePill || 'SAVE',
    size12oz: root.dataset.i18nSize12oz || '12OZ',
    upsellAdded: root.dataset.i18nUpsellAdded || '✓ ADDED',
    adding: root.dataset.i18nAdding || 'ADDING…',
    added: root.dataset.i18nAdded || 'ADDED!',
    error: root.dataset.i18nError || 'ERROR - TRY AGAIN',
  };

  const curatedEl = root.parentNode.querySelector('[data-dk-curated]')
    || document.querySelector('[data-dk-curated]');
  const curated = curatedEl ? JSON.parse(curatedEl.textContent || '[]') : [];

  const mugEl = root.parentNode.querySelector('[data-dk-mug]')
    || document.querySelector('[data-dk-mug]');
  const mugData = mugEl ? JSON.parse(mugEl.textContent || '{}') : null;

  // Pricing: live variant prices + the gift-engine bundle target are the
  // source of truth; the section settings only remain as fallbacks so the
  // page still renders if the metafield or price data is missing.
  const pricingEl = root.parentNode.querySelector('[data-dk-pricing]')
    || document.querySelector('[data-dk-pricing]');
  let bundleTarget = null;
  if (pricingEl) {
    try {
      const pr = JSON.parse(pricingEl.textContent || '{}');
      const tiers = (pr.config && pr.config.tiers) || [];
      for (const t of tiers) {
        if (t && t.type === 'combo' && t.active !== false && t.bundle_total
            && typeof t.bundle_total[pr.currency] === 'number') {
          bundleTarget = t.bundle_total[pr.currency] / 100;
          break;
        }
      }
    } catch (e) { /* fall back to section settings */ }
  }
  const wholeBagPrice = (p) => {
    const v = findVariantByGrind(p, 'whole');
    return v && typeof v.price === 'number' ? v.price / 100 : null;
  };
  const realBagPrices = curated.map(wholeBagPrice).filter((x) => x != null);
  const pricesComplete = curated.length > 0 && realBagPrices.length === curated.length;
  const retailBagsTotal = pricesComplete
    ? realBagPrices.reduce((a, b) => a + b, 0)
    : (parseFloat(root.dataset.bagRetail) || 22) * 3;
  const mugRetail = mugData && typeof mugData.price === 'number'
    ? mugData.price / 100
    : (parseFloat(root.dataset.mugRetail) || 24);
  const kitBase = bundleTarget != null
    ? bundleTarget
    : (parseFloat(root.dataset.kitPrice) || 54);
  const fourthAddonFallback = parseFloat(root.dataset.fourthAddon) || 18;
  function fourthPrice() {
    if (!state.fourth) return 0;
    const v = findVariantByGrind({ variants: state.fourth.variants || [] }, state.grind);
    return v && typeof v.price === 'number' ? v.price / 100 : fourthAddonFallback;
  }

  // Find the right variant for a product based on grind selection
  function findVariantByGrind(product, grind) {
    if (!product || !product.variants) return null;
    var grindLower = (grind || '').toLowerCase();
    // Map grind state to option values
    var grindTerms = grindLower === 'whole'
      ? ['whole bean', 'whole', 'en grain', 'en grains', 'grains']
      : ['ground', 'moulu', 'moulue'];

    // Try to find a 12oz / 340g variant with the right grind
    var sizeTerms = ['12 oz', '12oz', '340 g', '340g'];

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.available) continue;
      var opts = (v.options || []).map(function(o) { return o.toLowerCase().trim(); });
      var hasGrind = grindTerms.some(function(g) { return opts.some(function(o) { return o === g; }); });
      var hasSize = sizeTerms.some(function(s) { return opts.some(function(o) { return o === s; }); });
      if (hasGrind && hasSize) return v;
    }
    // Fallback: just match grind, any size
    for (var j = 0; j < product.variants.length; j++) {
      var v2 = product.variants[j];
      if (!v2.available) continue;
      var opts2 = (v2.options || []).map(function(o) { return o.toLowerCase().trim(); });
      var hasGrind2 = grindTerms.some(function(g) { return opts2.some(function(o) { return o === g; }); });
      if (hasGrind2) return v2;
    }
    // Final fallback: first available
    return product.variants.find(function(v3) { return v3.available; }) || product.variants[0];
  }

  const state = {
    grind: 'whole',
    fourth: null,     // { id, handle, title } or null
    sub: false,
    planId: '',
  };

  // ── Elements ─────────────────────────────────────────
  const $unit = root.querySelector('[data-unit-price]');
  const $retailInline = root.querySelector('[data-retail-inline]');
  const $retailTotal = root.querySelector('[data-retail-total]');
  const $youSave = root.querySelector('[data-you-save]');
  const $savePill = root.querySelector('[data-save-pill]');
  const $ctaPrice = root.querySelector('[data-cta-price]');
  const $curatedNames = root.querySelector('[data-curated-names]');

  const $fourthRow = root.querySelector('[data-fourth-row]');
  const $fourthName = root.querySelector('[data-fourth-name]');
  const $fourthPriceEl = root.querySelector('[data-fourth-price]');
  const $fourthClear = root.querySelector('[data-fourth-clear]');
  const $fourthBagSlot = root.querySelector('[data-bag-slot="fourth"]');
  const $fourthMount = root.querySelector('[data-fourth-mount]');

  const $subWrap = root.querySelector('[data-sub-wrap]');
  const $subToggle = root.querySelector('[data-sub-toggle]');
  const $subFreq = root.querySelector('[data-sub-freq]');
  const $subBadge = root.querySelector('[data-sub-badge]');

  const $add = root.querySelector('[data-kit-add]');
  const $gift = root.querySelector('[data-kit-gift]');

  // Populate curated names sub-line
  if ($curatedNames && curated.length) {
    $curatedNames.textContent = curated.map((c) => c.title).join(' · ');
  }

  // ── Grind chips ──────────────────────────────────────
  // Mug design picker: honor the customer's choice in the cart add.
  root.querySelectorAll('[data-mug-pick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!mugData) return;
      const vid = parseInt(btn.dataset.mugVariantId, 10);
      if (Number.isFinite(vid)) {
        mugData.id = vid;
        if (btn.dataset.mugTitle) mugData.title = btn.dataset.mugTitle;
      }
    });
  });

  root.querySelectorAll('[data-grind]').forEach((chip) => {
    chip.addEventListener('click', () => {
      root.querySelectorAll('[data-grind]').forEach((c) => {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
      state.grind = chip.dataset.grind;
    });
  });

  // ── 4th-bag picker ───────────────────────────────────
  root.querySelectorAll('[data-fourth-pick]').forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.dataset.productId;
      const title = card.dataset.productTitle;
      const handle = card.dataset.productHandle;
      const image = card.dataset.productImage || '';
      let variants = [];
      try { variants = JSON.parse(card.dataset.productVariants || '[]'); } catch (e) {}

      if (state.fourth && state.fourth.id === id) {
        state.fourth = null;
      } else {
        state.fourth = { id, handle, title, image, variants };
      }
      render();
    });
  });
  if ($fourthClear) {
    $fourthClear.addEventListener('click', (e) => {
      e.stopPropagation();
      state.fourth = null;
      render();
    });
  }

  // ── Subscribe toggle + freq ──────────────────────────
  if ($subToggle) {
    $subToggle.addEventListener('click', () => {
      state.sub = !state.sub;
      render();
    });
  }
  root.querySelectorAll('[data-plan]').forEach((chip) => {
    chip.addEventListener('click', () => {
      root.querySelectorAll('[data-plan]').forEach((c) => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      state.planId = chip.dataset.plan;
    });
  });
  // Pick default plan if subscribe group exists
  const firstPlan = root.querySelector('[data-plan].is-active');
  if (firstPlan) state.planId = firstPlan.dataset.plan;

  // ── Render ───────────────────────────────────────────
  function render() {
    const hasFourth = !!state.fourth;
    root.dataset.hasFourth = hasFourth ? '1' : '0';

    const subtotal = kitBase + (hasFourth ? fourthPrice() : 0);
    const unit = state.sub ? subtotal * 0.85 : subtotal;
    const retailTotal = retailBagsTotal + mugRetail + (hasFourth ? fourthPrice() : 0);
    const save = retailTotal - unit;

    $unit.textContent = '$' + unit.toFixed(2);
    $ctaPrice.textContent = '$' + unit.toFixed(2);
    if ($retailInline) $retailInline.textContent = '$' + retailTotal.toFixed(2);
    if ($retailTotal) $retailTotal.textContent = '$' + retailTotal.toFixed(0);
    if ($youSave) $youSave.textContent = saveLabel + ' $' + save.toFixed(0);
    if ($savePill) $savePill.textContent = i18n.savePill + ' $' + save.toFixed(0);
    if ($subBadge) $subBadge.textContent = '−$' + (subtotal * 0.15).toFixed(2);

    // 4th-bag row
    if ($fourthRow) {
      $fourthRow.hidden = !hasFourth;
      if (hasFourth && $fourthName) $fourthName.textContent = state.fourth.title.toUpperCase() + ' · ' + i18n.size12oz;
    }
    if ($fourthPriceEl && hasFourth) $fourthPriceEl.textContent = '+$' + fourthPrice().toFixed(2);
    if ($fourthClear) $fourthClear.hidden = !hasFourth;

    // Upsell card active state
    root.querySelectorAll('[data-fourth-pick]').forEach((card) => {
      const isActive = hasFourth && card.dataset.productId === state.fourth.id;
      card.classList.toggle('is-active', isActive);
      const addEl = card.querySelector('[data-upsell-add]');
      if (addEl) {
        if (!addEl.dataset.orig) addEl.dataset.orig = addEl.textContent;
        addEl.textContent = isActive ? i18n.upsellAdded : addEl.dataset.orig;
      }
    });

    // Bag visual (4th slot shown/hidden — actual bag image is a label stand-in)
    if ($fourthBagSlot) {
      $fourthBagSlot.hidden = !hasFourth;
      if (hasFourth && $fourthMount) {
        $fourthMount.textContent = '';
        var link = document.createElement('a');
        link.href = '/products/' + state.fourth.handle;
        link.className = 'gr-dk__bag-fourth-link';
        link.style.cssText = 'display:block;width:105px;height:140px;text-decoration:none;';
        if (state.fourth.image) {
          var img = document.createElement('img');
          img.src = state.fourth.image;
          img.alt = state.fourth.title;
          img.style.cssText = 'width:100%;height:100%;object-fit:contain;';
          img.loading = 'lazy';
          link.appendChild(img);
        } else {
          link.style.cssText += 'background:linear-gradient(180deg,#2a1d12,#0A0A0A);border:1px solid var(--gr-orange);border-radius:8px;padding:10px;font-family:var(--gr-font-heading);font-size:9px;letter-spacing:0.15em;color:var(--gr-fg);text-align:center;display:flex;align-items:center;justify-content:center;';
          link.textContent = state.fourth.title.toUpperCase();
        }
        $fourthMount.appendChild(link);
      }
    }

    // Subscribe state
    if ($subWrap) $subWrap.classList.toggle('is-active', state.sub);
    if ($subFreq) $subFreq.hidden = !state.sub;
  }

  // ── Add to cart (individual items) ──────────────────
  $add.addEventListener('click', async () => {
    if ($add.disabled || curated.length === 0) return;
    $add.disabled = true;
    const origLabel = $add.textContent;
    $add.textContent = i18n.adding;

    const items = [];
    const giftMsg = ($gift && $gift.value.trim()) ? $gift.value.trim() : null;

    // Add each curated coffee with the right grind variant
    curated.forEach(function(product) {
      var variant = findVariantByGrind(product, state.grind);
      if (!variant) return;
      var item = {
        id: variant.id,
        quantity: 1,
        properties: {
          _discovery_kit: 'true',
          _kit_grind: state.grind,
          'Bundle': 'Discovery Kit'
        }
      };
      if (giftMsg) item.properties['Gift message'] = giftMsg;
      if (state.sub && state.planId) item.selling_plan = parseInt(state.planId);
      items.push(item);
    });

    // Add the 4th bag if selected — resolve to the variant matching the
    // chosen grind so the picker affects this bag too.
    if (state.fourth && state.fourth.id) {
      var fourthVariant = findVariantByGrind({ variants: state.fourth.variants || [] }, state.grind);
      var fourthVariantId = fourthVariant ? fourthVariant.id : parseInt(state.fourth.id);
      var fourthItem = {
        id: parseInt(fourthVariantId),
        quantity: 1,
        properties: {
          _discovery_kit: 'true',
          _kit_addon: 'true',
          _kit_grind: state.grind,
          'Bundle': 'Discovery Kit'
        }
      };
      if (state.sub && state.planId) fourthItem.selling_plan = parseInt(state.planId);
      items.push(fourthItem);
    }

    // Add the mug
    if (mugData && mugData.id) {
      items.push({
        id: mugData.id,
        quantity: 1,
        properties: { _discovery_kit: 'true', _kit_mug: 'true', 'Bundle': 'Discovery Kit' }
      });
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(function() { return {}; });
        throw new Error(errData.description || 'Cart add failed');
      }
      $add.textContent = i18n.added;
      setTimeout(function() { $add.textContent = origLabel; }, 1500);
      window.dispatchEvent(new CustomEvent('gr:cart:open'));
      window.dispatchEvent(new CustomEvent('gr:cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error('Discovery Kit cart error:', err.message);
      $add.textContent = i18n.error;
      setTimeout(function() { $add.textContent = origLabel; }, 2000);
    } finally {
      $add.disabled = false;
    }
  });

  render();
})();
